import { useControllableProp } from '@chakra-ui/react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  type ActionMeta,
  type InputActionMeta,
  type ValueType,
  type GroupedOptionsType,
} from 'react-select';

import { TeamMemberData, useTeamMemberList } from '@frontend/api';
import { Select, type SelectProps } from '@frontend/components';
import { isObject } from '@frontend/utils';

import {
  UNASSIGNED_LABEL,
  UNASSIGNED_OPTION,
  UNASSIGNED_VALUE,
} from 'modules/common/smartComponents/const';

import { getGroupId } from 'store/session/selectors';

import { Control, Option, SingleValue } from './components';
import { ANYBODY_OPTION, ANYBODY_VALUE, ANYBODY_LABEL } from './const';
import { sortByName } from './utils';

export type TeamMemberOptionType = Partial<TeamMemberData> & {
  value: number | typeof ANYBODY_VALUE | typeof UNASSIGNED_VALUE;
  label: string;
};

export type TeamMemberSelectProps<IsMulti extends boolean = boolean> =
  SelectProps<TeamMemberOptionType, IsMulti> & {
    mode: 'assignment' | 'assignmentFilter' | 'default';
    isExportControlled?: boolean;
    /** Override the locator used to specify user group. When provided (including empty string), takes precedence over the current group from the Redux store. */
    locator?: string;
    overrideOptions?: (
      options: GroupedOptionsType<TeamMemberOptionType>,
    ) => GroupedOptionsType<TeamMemberOptionType>;
    value?: ValueType<TeamMemberOptionType, IsMulti> | number[] | null;
    defaultValue?: number | TeamMemberOptionType;
    allowMixUnassigned?: boolean;
  };

const InnerTeamMemberSelect = forwardRef<'div', TeamMemberSelectProps>(
  (
    {
      mode = 'default',
      isExportControlled = false,
      locator: locatorProp,
      placeholder = mode === 'assignment' ? UNASSIGNED_LABEL : ANYBODY_LABEL,
      noOptionsMessage = () => 'No matching users',
      overrideOptions = (options) => options,
      defaultValue,
      value: valueProp,
      isMulti,
      isLoading,
      onChange,
      onInputChange,
      isClearable = true,
      allowMixUnassigned = false,
      components,
      ...restProps
    },
    ref,
  ) => {
    const allowAnybodyOption = mode === 'default';
    const allowUnassignedOption = mode !== 'default';
    // const defaultToUnassigned = mode === 'assignment'
    const defaultLocator = useSelector(getGroupId);
    const locator = locatorProp !== undefined ? locatorProp : defaultLocator; // use the locator prop if provided, otherwise use the current group from the Redux store
    const [searchTerm, setSearchTerm] = useState('');

    const { data: teamMembers = [], isFetching } = useTeamMemberList(
      { locator: locator ?? '' },
      {
        query: { search: searchTerm, controlled: isExportControlled },
        queryOptions: { enabled: !!locator },
      },
    );

    // create options list from team members list
    const teamMemberOptions = useMemo(
      () =>
        teamMembers
          .sort(sortByName)
          .reduce<TeamMemberOptionType[]>((out, teamMember, index) => {
            // dedupe team members and transform to option shape
            if (
              teamMembers.every(
                (otherMember, otherIndex) =>
                  otherMember.userId !== teamMember.userId ||
                  otherIndex === index,
              )
            ) {
              out.push({
                value: teamMember.userId,
                label: `${teamMember.firstName} ${teamMember.lastName}`,
                ...teamMember,
              });
            }

            return out;
          }, []),
      [teamMembers],
    );

    const [valueState, setValueState] =
      useState<ValueType<TeamMemberOptionType, boolean>>(valueProp);

    const transformValueToOption = useCallback(
      (
        val?:
          | number
          | TeamMemberOptionType
          | null
          | Array<number>
          | Array<TeamMemberOptionType>
          | 'UNASSIGNED'
          | 'ANYONE_VALUE',
      ) => {
        if (!val) return val;
        const arr = Array.isArray(val) ? val : [val];
        const transformed =
          arr
            ?.map((v) =>
              typeof v === 'number'
                ? teamMemberOptions
                    .concat(valueState ?? []) // check valueState to look for selected values that might not be in search results
                    .find(({ value }) => value === v)
                : v === UNASSIGNED_VALUE
                ? UNASSIGNED_OPTION
                : v === ANYBODY_VALUE
                ? ANYBODY_OPTION
                : v,
            )
            .filter(Boolean) ?? val;

        return isMulti ? transformed : transformed[0];
      },
      [isMulti, teamMemberOptions, valueState],
    );

    const transformedValueProp = useMemo(
      () => transformValueToOption(valueProp),
      [valueProp, transformValueToOption],
    );
    const transformedDefaultValue = useMemo(
      () => transformValueToOption(defaultValue),
      [defaultValue, transformValueToOption],
    );
    const [selectedOptions, setSelectedOptions] = useState<
      TeamMemberOptionType[]
    >([]);

    const [, value] = useControllableProp(transformedValueProp, valueState);

    const options = useMemo(
      () =>
        overrideOptions([
          {
            options:
              allowAnybodyOption &&
              (Array.isArray(value) ? value.length !== 0 : !!value)
                ? [ANYBODY_OPTION]
                : allowUnassignedOption &&
                  (isMulti ||
                    value ||
                    (value !== null && transformedDefaultValue))
                ? [UNASSIGNED_OPTION]
                : [],
          },
          {
            options: [
              ...selectedOptions.filter(
                (option) => !teamMemberOptions.includes(option),
              ),
              ...teamMemberOptions,
            ],
          },
        ]),
      [
        allowAnybodyOption,
        allowUnassignedOption,
        isMulti,
        overrideOptions,
        selectedOptions,
        teamMemberOptions,
        value,
        transformedDefaultValue,
      ],
    );

    const handleInputChange = useCallback(
      (value: string, meta: InputActionMeta) => {
        // set search query
        setSearchTerm(value);

        // raise change
        onInputChange?.(value, meta);
      },
      [onInputChange],
    );

    const handleChange = useCallback(
      (
        newValue: TeamMemberOptionType | TeamMemberOptionType[] | null,
        meta: ActionMeta<TeamMemberOptionType>,
      ) => {
        let nextValue = newValue;

        if (Array.isArray(nextValue)) {
          if (meta.option === ANYBODY_OPTION) {
            nextValue = [];
          } else if (allowMixUnassigned) {
            nextValue = nextValue.filter((option) => option !== ANYBODY_OPTION);
          } else if (meta.option === UNASSIGNED_OPTION) {
            nextValue = [UNASSIGNED_OPTION];
          } else {
            nextValue = nextValue.filter(
              (option) =>
                option !== UNASSIGNED_OPTION && option !== ANYBODY_OPTION,
            );
          }
        } else if (
          isMulti ||
          nextValue === UNASSIGNED_OPTION ||
          nextValue === ANYBODY_OPTION
        ) {
          nextValue = [];
        }

        // store selected options incase they are not available after a search query
        setSelectedOptions(
          nextValue && !Array.isArray(nextValue)
            ? [nextValue]
            : nextValue ?? [],
        );

        // cache selected values
        setValueState(nextValue);

        // raise change
        onChange?.(nextValue, meta);
      },
      [isMulti, onChange, allowMixUnassigned],
    );

    return (
      <Select
        ref={ref}
        key={
          Array.isArray(transformedDefaultValue)
            ? transformedDefaultValue
                .map((opt) => (isObject(opt) ? opt.value : opt))
                .join(',')
            : isObject(transformedDefaultValue)
            ? transformedDefaultValue.value
            : transformedDefaultValue
        }
        components={{
          Option,
          Control,
          SingleValue,
          ...components,
        }}
        placeholder={placeholder}
        noOptionsMessage={noOptionsMessage}
        isMulti={isMulti}
        defaultValue={transformedDefaultValue}
        value={value}
        options={options}
        filterOption={null}
        isClearable={isClearable}
        onInputChange={handleInputChange}
        onChange={handleChange}
        isLoading={isLoading || isFetching} // override isLoading based on fetch
        {...restProps}
      />
    );
  },
);
InnerTeamMemberSelect.displayName = 'InnerTeamMemberSelect';

export const TeamMemberSelect = forwardRef<'div', TeamMemberSelectProps>(
  (props, ref) => {
    const defaultValue = props.defaultValue;
    return (
      <InnerTeamMemberSelect
        {...props}
        ref={ref}
        // using typeof for better typescript type inference
        key={typeof defaultValue !== 'object' ? defaultValue : undefined}
      />
    );
  },
);
TeamMemberSelect.displayName = 'TeamMemberSelect';
